import { ArrowRight, BookOpen, Brain, Clock3, Gauge, HeartPulse, Route, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import type { ChildProfile, ContentNode, Surface } from "../../types";
import { masteryLabel, minutesLabel, nextActionForSkill } from "../../lib/progressionEngine";

interface ParentDashboardProps {
  children: ChildProfile[];
  recommendedNode: ContentNode;
  selectedChild: ChildProfile;
  selectedChildId: string;
  setActiveSurface: (surface: Surface) => void;
  setSelectedChildId: (childId: string) => void;
}

export function ParentDashboard({
  children,
  recommendedNode,
  selectedChild,
  selectedChildId,
  setActiveSurface,
  setSelectedChildId
}: ParentDashboardProps) {
  const readingAverage = averageForSubject(selectedChild, "Reading");
  const mathsAverage = averageForSubject(selectedChild, "Maths");
  const fragileSkills = selectedChild.skills.filter((skill) => skill.mastery === "learning" || skill.mastery === "review due");

  return (
    <div className="dashboard-grid parent-grid">
      <section className="surface-panel child-selector-panel" aria-labelledby="children-title">
        <div className="panel-heading">
          <h3 id="children-title">Children</h3>
          <span>This week</span>
        </div>
        <div className="child-selector">
          {children.map((child) => (
            <button
              className={child.id === selectedChildId ? "child-chip is-active" : "child-chip"}
              key={child.id}
              onClick={() => setSelectedChildId(child.id)}
              type="button"
            >
              <span aria-hidden="true">{child.avatar}</span>
              <strong>{child.name}</strong>
              <small>Age {child.age}</small>
            </button>
          ))}
        </div>
        <div className="progress-list">
          <ProgressRow icon={<BookOpen size={18} />} label="Reading" value={readingAverage} detail="Phonics, fluency, comprehension" />
          <ProgressRow icon={<Brain size={18} />} label="Maths" value={mathsAverage} detail="Number, operations, problem solving" />
          <ProgressRow icon={<Route size={18} />} label="Path stability" value={selectedChild.focusScore} detail={selectedChild.currentPath} />
        </div>
      </section>

      <section className="surface-panel insight-panel" aria-labelledby="insight-title">
        <div className="panel-heading">
          <h3 id="insight-title">What changed</h3>
          <span>{selectedChild.yearGroup}</span>
        </div>
        <div className="stat-grid">
          <Stat icon={<Clock3 size={18} />} label="Learning time" value={minutesLabel(selectedChild.weeklyMinutes)} />
          <Stat icon={<Gauge size={18} />} label="Focused effort" value={`${selectedChild.focusScore}%`} />
          <Stat icon={<HeartPulse size={18} />} label="Support signals" value={String(selectedChild.frustrationSignals)} />
        </div>
        <div className="next-card">
          <span className="mini-icon" aria-hidden="true">
            <BookOpen size={20} />
          </span>
          <div>
            <strong>Next for {selectedChild.name}</strong>
            <p>{recommendedNode.subject}: {recommendedNode.instructionScript}</p>
          </div>
          <button className="icon-button" onClick={() => setActiveSurface("child")} type="button" aria-label="Open child lesson">
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      <section className="surface-panel skill-panel" aria-labelledby="skills-title">
        <div className="panel-heading">
          <h3 id="skills-title">Secure and fragile skills</h3>
          <span>Explainable adaptation</span>
        </div>
        <div className="skill-stack">
          {selectedChild.skills.map((skill) => (
            <article className="skill-row" key={skill.id}>
              <div>
                <strong>{skill.label}</strong>
                <p>{skill.strand} - {skill.ageRespectfulTheme}</p>
              </div>
              <span className={`mastery-state mastery-${skill.mastery.replace(" ", "-")}`}>{masteryLabel(skill)}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="surface-panel action-panel" aria-labelledby="actions-title">
        <div className="panel-heading">
          <h3 id="actions-title">Home actions</h3>
          <ShieldCheck size={19} aria-hidden="true" />
        </div>
        <div className="action-list">
          {fragileSkills.map((skill) => (
            <article key={skill.id}>
              <strong>{skill.label}</strong>
              <p>{nextActionForSkill(skill)}</p>
            </article>
          ))}
          <article>
            <strong>Off-screen extension</strong>
            <p>{recommendedNode.parentExtension}</p>
          </article>
        </div>
      </section>
    </div>
  );
}

function ProgressRow({
  detail,
  icon,
  label,
  value
}: {
  detail: string;
  icon: ReactNode;
  label: string;
  value: number;
}) {
  return (
    <article className="progress-row">
      <span className="mini-icon" aria-hidden="true">{icon}</span>
      <div>
        <strong>{label}</strong>
        <small>{detail}</small>
        <span className="progress-track"><span style={{ width: `${value}%` }} /></span>
      </div>
      <b>{value}%</b>
    </article>
  );
}

function Stat({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <article className="stat-card">
      <span className="mini-icon" aria-hidden="true">{icon}</span>
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function averageForSubject(child: ChildProfile, subject: "Reading" | "Maths") {
  const subjectSkills = child.skills.filter((skill) => skill.subject === subject);
  if (subjectSkills.length === 0) return 0;
  return Math.round((subjectSkills.reduce((sum, skill) => sum + skill.accuracy, 0) / subjectSkills.length) * 100);
}
